import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from './AuthContext';
import { useDevRoleDashboard } from './DevRoleDashboardContext';
import type { MenuKey, UserPermissions } from '../types/permissions';
import { SUPER_ADMIN_EMAIL } from '../types/permissions';

interface PermissionsContextType {
  permissions: UserPermissions[];
  hasPermission: (menuKey: MenuKey) => boolean;
  isSuperAdmin: boolean;
  isLoading: boolean;
  /** Rafraîchit les permissions. Si options.background === true, pas de setIsLoading(true) (évite re-renders / "reload"). */
  refreshPermissions: (options?: { background?: boolean }) => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const devRole = useDevRoleDashboard();
  const [permissions, setPermissions] = useState<UserPermissions[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const fetchPermissions = async (skipLoadingState = false) => {
    if (!user?.email) {
      setPermissions([]);
      setIsSuperAdmin(false);
      setIsLoading(false);
      return;
    }

    try {
      if (!skipLoadingState) setIsLoading(true);

      const roleOverride = import.meta.env.DEV && devRole?.devRoleIdOverride;

      // Super admin = toujours selon le compte connecté (accès Administration conservé en test rôle)
      const isSuper = user.email === SUPER_ADMIN_EMAIL;
      setIsSuperAdmin(isSuper);

      if (roleOverride) {
        // Mode "Rôle test" : permissions navbar/sidebar = rôle simulé (sans toucher à isSuperAdmin)
        const { data, error } = await supabase.rpc('get_permissions_by_role_id', {
          p_role_id: roleOverride,
        });
        if (error) {
          setPermissions([]);
        } else {
          setPermissions((data || []) as UserPermissions[]);
        }
      } else {
        const { data, error } = await supabase.rpc('get_user_permissions', {
          user_email: user.email,
        });
        if (error) {
          setPermissions([]);
        } else {
          setPermissions(data || []);
        }
      }
    } catch (error) {
      setPermissions([]);
    } finally {
      if (!skipLoadingState) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.email) {
      setIsLoading(true);
    }
    fetchPermissions();
  }, [user?.email, devRole?.devRoleIdOverride]);

  const hasPermission = (menuKey: MenuKey): boolean => {
    // En mode "Rôle test", utiliser les permissions du rôle simulé (navbar/sidebar)
    const roleOverride = import.meta.env.DEV && devRole?.devRoleIdOverride;
    if (roleOverride) {
      const permission = permissions.find(p => p.menu_key === menuKey);
      return permission?.is_enabled ?? false;
    }
    // Super admin (hors test rôle) a tous les accès
    if (isSuperAdmin) {
      return true;
    }
    const permission = permissions.find(p => p.menu_key === menuKey);
    return permission?.is_enabled ?? false;
  };

  const refreshPermissions = async (options?: { background?: boolean }) => {
    await fetchPermissions(options?.background === true);
  };

  return (
    <PermissionsContext.Provider
      value={{
        permissions,
        hasPermission,
        isSuperAdmin,
        isLoading,
        refreshPermissions,
      }}
    >
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};
